import React from "react";
import { Link as RLink } from "react-router-dom";

import { publicUrl } from "../../config";
import { RADARS, RADAR_SLUGS } from "../../radars";
import ElyadataWordmark from "../ElyadataWordmark/ElyadataWordmark";
import "./site-footer.scss";

type Props = {
  /** Newest published edition, e.g. `Q2 2026`. */
  latestQuarter?: string;
};

/**
 * Anchors on the radars home are reached from any page, so they are written as
 * document links rather than router links: from a radar page the browser loads
 * `/` and jumps to the fragment on its own, and on the home page itself it is a
 * same document scroll.
 */
const homeAnchor = (fragment: string) => `${publicUrl}#${fragment}`;

const SITE = "https://www.elyadata.com/en";

/** Columns, labels, destinations and office addresses of elyadata.com's footer. */
const COMPANY_LINKS = [
  { label: "Why Us", href: `${SITE}/about` },
  { label: "Journal", href: `${SITE}/journal` },
  { label: "Success Stories", href: `${SITE}/success-stories` },
  { label: "Research", href: `${SITE}/research` },
];

const CONNECT_LINKS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/elyadata" },
  { label: "GitHub", href: "https://github.com/elyadata" },
  { label: "X (Twitter)", href: "https://x.com/ElyadataHub" },
  { label: "Medium", href: "https://insights.elyadata.com" },
];

const OFFICES = [
  { city: "Paris", address: "128 rue La Boetie, Paris 75008" },
  {
    city: "Tunis",
    address: "Rue de l'île de Failaka, Immeuble HBG, Les berges du lac 2",
  },
];

const External = ({ href, label }: { href: string; label: string }) => (
  <a
    className="site-footer__link"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    {label}
  </a>
);

/**
 * The footer of elyadata.com — same wording, same columns, same offices — with
 * one radar specific column added, shared by every page of this site.
 */
export default function SiteFooter({ latestQuarter }: Props) {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__brand">
          <ElyadataWordmark className="site-footer__logo" />
          <p className="site-footer__blurb">
            AI &amp; Data Engineering firm. Bridging business, IT, AI
            engineering and scientific research to ship production-grade
            systems.
          </p>
        </div>

        <nav className="site-footer__column" aria-label="Radars">
          <span className="site-footer__column-title">Radars</span>
          {RADAR_SLUGS.map((slug) => (
            <RLink
              key={slug}
              className="site-footer__link"
              to={`/${slug}/index.html`}
            >
              {RADARS[slug].shortName}
            </RLink>
          ))}
          <a className="site-footer__link" href={homeAnchor("archive")}>
            Past editions
          </a>
          <a className="site-footer__link" href={homeAnchor("method")}>
            How we decide
          </a>
        </nav>

        <nav className="site-footer__column" aria-label="Company">
          <span className="site-footer__column-title">Company</span>
          {COMPANY_LINKS.map((link) => (
            <External key={link.label} {...link} />
          ))}
        </nav>

        <nav className="site-footer__column" aria-label="Connect">
          <span className="site-footer__column-title">Connect</span>
          {CONNECT_LINKS.map((link) => (
            <External key={link.label} {...link} />
          ))}
        </nav>

        <div className="site-footer__column">
          <span className="site-footer__column-title">Offices</span>
          {OFFICES.map((office) => (
            <p className="site-footer__office" key={office.city}>
              <span className="site-footer__office-city">{office.city}</span>
              {office.address}
            </p>
          ))}
        </div>
      </div>

      <div className="site-footer__inner site-footer__inner--end">
        <div className="site-footer__end">
          <span>
            © {new Date().getFullYear()} Elyadata. All rights reserved.
          </span>
          <External href={`${SITE}/privacy-policy`} label="Privacy Policy" />
          {latestQuarter && (
            <span className="site-footer__edition">
              {latestQuarter} · edition current
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
